/**
 * notifSync.js
 * مزامنة المفضلة مع تفضيلات الإشعارات في MongoDB
 * Single Source of Truth → fcm_preferences (per device_id)
 */
import api from '../api/api';

const DEVICE_KEY = 'rtm_device_id';
const FAV_KEY    = 'rtm_favorites';
const NOTIF_PREF_KEY = 'rtm_notif_types'; // notification_types stored locally

export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = 'dev_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function getLocalFavs() {
  try { return JSON.parse(localStorage.getItem(FAV_KEY)) || { teams: [], leagues: [] }; }
  catch { return { teams: [], leagues: [] }; }
}

export function getLocalNotifTypes() {
  try {
    const saved = JSON.parse(localStorage.getItem(NOTIF_PREF_KEY));
    return saved || { goals: true, kickoff: true, halftime: true, fulltime: true, reports: true, red_cards: true, match_reminders: true };
  } catch {
    return { goals: true, kickoff: true, halftime: true, fulltime: true, reports: true, red_cards: true, match_reminders: true };
  }
}

/**
 * مزامنة المفضلة مع MongoDB
 * يُستدعى عند: تغيير المفضلة من MyTeams / OnboardingModal
 */
export async function syncFavsToCloud(favs, notifTypes = null) {
  try {
    const deviceId = getDeviceId();
    const nt = notifTypes || getLocalNotifTypes();
    const leagues = (favs.leagues || []).map(l => String(l.id || l));
    const teams   = (favs.teams   || []).map(t =>
      typeof t === 'object' ? { id: t.id, name: t.name || t.name_ar, logo: t.logo || '' } : { id: t, name: null, logo: null }
    );
    // اشتق filter_type الصحيح بناءً على الفرق والدوريات المختارة
    let filter_type = 'all';
    if (leagues.length > 0 && teams.length > 0) filter_type = 'both';
    else if (leagues.length > 0) filter_type = 'leagues';
    else if (teams.length > 0) filter_type = 'teams';

    await api.post('/notifications/follow-preferences', {
      device_id: deviceId,
      leagues,
      teams,
      filter_type,
      notification_types: nt,
    });
  } catch { /* silent — non-blocking */ }
}

/**
 * جلب تفضيلات الإشعارات من MongoDB ودمجها مع rtm_favorites
 * يُستدعى عند: تحميل NotificationsPreferencesPage
 */
export async function loadAndMergePrefs() {  const deviceId = getDeviceId();
  const localFavs = getLocalFavs();

  // Local favorites → IDs
  const favLeagueIds = (localFavs.leagues || []).map(l => parseInt(l.id || l)).filter(Boolean);
  const favTeams     = (localFavs.teams   || []).map(t =>
    typeof t === 'object'
      ? { id: t.id, name: t.name || t.name_ar || null, logo: t.logo || null }
      : { id: parseInt(t), name: null, logo: null }
  ).filter(t => t.id);

  try {
    const res = await api.get(`/notifications/follow-preferences?device_id=${deviceId}`);
    const remote = res?.data ?? res;
    if (!remote || typeof remote !== 'object') throw new Error('invalid');

    // Merge: combine remote leagues/teams with local favorites (no duplicates)
    const remoteLIds = new Set((remote.leagues || []).map(Number));
    const mergedLeagueIds = [...new Set([...Array.from(remoteLIds), ...favLeagueIds])].map(Number);

    const remoteTeamIds = new Set((remote.teams || []).map(t => (typeof t === 'object' ? t.id : t)));
    const extraTeams = favTeams.filter(t => !remoteTeamIds.has(t.id));
    const mergedTeams = [...(remote.teams || []), ...extraTeams];

    return {
      leagues: mergedLeagueIds,
      teams: mergedTeams,
      filter_type: remote.filter_type || 'all',
      notification_types: {
        goals: true, kickoff: true, halftime: true,
        fulltime: true, reports: true, red_cards: true,
        match_reminders: true,
        ...(remote.notification_types || {}),
      },
    };
  } catch {
    // Fallback to local favorites
    return {
      leagues: favLeagueIds,
      teams: favTeams,
      filter_type: favLeagueIds.length > 0 ? 'leagues' : 'all',
      notification_types: getLocalNotifTypes(),
    };
  }
}

/**
 * فلتر الإشعارات الداخلية — هل يجب إظهار هذا الإشعار للمستخدم؟
 * يقرأ التفضيلات من localStorage (cache) لتجنب طلب شبكة synchronous
 * data: الـ data object من FCM payload (يحتوي على league_id, teams, type)
 */
export function shouldShowInAppNotification(data = {}) {
  try {
    // جلب التفضيلات المحلية (cache من آخر تحميل للصفحة)
    const cachedPrefs = (() => {
      try { return JSON.parse(localStorage.getItem('rtm_notif_cache') || 'null'); }
      catch { return null; }
    })();
    if (!cachedPrefs) return true; // لا cache → اعرض الكل

    const { notification_types = {}, leagues = [], teams = [] } = cachedPrefs;

    // 1. تصفية بنوع الإشعار
    const typeMap = {
      goal:     'goals',
      goals:    'goals',
      kickoff:  'kickoff',
      halftime: 'halftime',
      fulltime: 'fulltime',
      reports:  'reports',
      red_card: 'red_cards',
      redcard:  'red_cards',
      match_reminder: 'match_reminders',
    };
    const ntKey = typeMap[data?.type];
    if (ntKey && notification_types[ntKey] === false) return false;

    // 2. تصفية بالبطولة / الفريق (إذا لم تُحدد دوريات/فرق → اعرض الكل)
    const hasLeaguePref = leagues.length > 0;
    const hasTeamPref   = teams.length   > 0;
    if (!hasLeaguePref && !hasTeamPref) return true;

    const payloadLeague = String(data?.league_id || '').trim();
    const payloadTeams  = String(data?.teams     || '').split(',').map(t => t.trim()).filter(Boolean);

    // مطابقة البطولة
    if (hasLeaguePref && payloadLeague) {
      if (leagues.map(String).includes(payloadLeague)) return true;
    }
    // مطابقة الفريق
    if (hasTeamPref && payloadTeams.length) {
      const prefTeamIds = teams.map(t => String(typeof t === 'object' ? t.id : t));
      if (payloadTeams.some(t => prefTeamIds.includes(t))) return true;
    }

    // لا مطابقة → لا تعرض
    return false;
  } catch {
    return true; // خطأ → اعرض (أفضل من إخفاء)
  }
}

/**
 * حفظ التفضيلات في cache localStorage للفلتر الفوري
 */
export function cacheNotifPrefs(prefs) {
  try {
    localStorage.setItem('rtm_notif_cache', JSON.stringify({
      leagues: prefs.leagues || [],
      teams:   prefs.teams   || [],
      notification_types: prefs.notification_types || {},
    }));
  } catch (err) {
    console.error('[notifSync] Failed to cache preferences to localStorage:', err);
  }
}
export async function savePrefsAndSync(prefs) {
  const deviceId = getDeviceId();

  // Save to MongoDB
  await api.post('/notifications/follow-preferences', {
    device_id: deviceId,
    ...prefs,
  });

  // Also update rtm_favorites localStorage to reflect the change
  try {
    const existing = getLocalFavs();
    const prefLeagueIds = new Set((prefs.leagues || []).map(Number));
    const prefTeamIds   = new Set((prefs.teams || []).map(t => typeof t === 'object' ? t.id : parseInt(t)));

    // Add newly followed leagues/teams to favorites if not already there
    const existingLeagueIds = new Set((existing.leagues || []).map(l => l.id || l));
    const newLeagues = (prefs.leagues || [])
      .map(Number)
      .filter(id => !existingLeagueIds.has(id))
      .map(id => ({ id, name: String(id), logo: '' }));

    const existingTeamIds = new Set((existing.teams || []).map(t => t.id || t));
    const newTeams = (prefs.teams || [])
      .filter(t => {
        const id = typeof t === 'object' ? t.id : parseInt(t);
        return !existingTeamIds.has(id);
      });

    if (newLeagues.length > 0 || newTeams.length > 0) {
      const updated = {
        leagues: [...(existing.leagues || []), ...newLeagues],
        teams:   [...(existing.teams   || []), ...newTeams],
      };
      localStorage.setItem('rtm_favorites', JSON.stringify(updated));
      window.dispatchEvent(new StorageEvent('storage', { key: 'rtm_favorites' }));
    }

    // Save notification_types locally
    if (prefs.notification_types) {
      localStorage.setItem(NOTIF_PREF_KEY, JSON.stringify(prefs.notification_types));
    }
  } catch { /* silent */ }
}
